import { getConfig } from '@client/config/runtime';
import { createLogger } from '@client/utils/logger';
import {
    challengeWidgetSize,
    ensureChallengeSurface,
    removeChallengeSurface,
    setChallengeInteractive,
} from './turnstile-challenge-surface';
import { loadTurnstileScript } from './turnstile-script-loader';
import { reportTurnstileTokenUnavailable, type TurnstileUnavailableReason } from './turnstile-unavailable-report';

const log = createLogger('TurnstileToken');

// Non-interactive verification normally settles in a couple of seconds. Keep its queue slot
// short; lifecycle callbacks replace this with a human-scale deadline only when Cloudflare
// escalates to an interactive challenge.
const TOKEN_TIMEOUT_MS = 15_000;
const INTERACTIVE_TOKEN_TIMEOUT_MS = 60_000;
// A background (speculative) mint is bounded by this deadline measured from ENQUEUE — queue
// wait included — so hover-triggered work can never hold foreground mints for the full
// per-slot window (CUR-901). Healthy mints settle in ~2s, so speculation still usually wins;
// under contention or a degraded Turnstile, speculative work is the first to yield.
const BACKGROUND_TOKEN_DEADLINE_MS = 5_000;

export type TurnstileMintPriority = 'foreground' | 'background';
export type TurnstileMintSettlementReason = 'deadline' | 'escalation-declined' | 'other';

/**
 * Priority handle for one Turnstile mint. Created by the caller that starts the request and
 * passed alongside it (for tRPC operations via `context.captchaMintControl`), so that when a
 * foreground actor later joins the same request — e.g. a tap joining a hover-initiated
 * checkpoint save — it can promote the shared mint instead of inheriting the short
 * speculative deadline.
 */
export interface TurnstileMintControl {
    readonly priority: TurnstileMintPriority;
    /**
     * Promote a pending or in-flight background mint to the standard foreground window.
     * Returns false only when a background-only constraint already ended the mint, signalling
     * that a foreground caller may need a fresh attempt. Returns true when promotion takes
     * effect, had already taken effect, or the mint ended for a priority-independent reason.
     */
    upgradeToForeground(): boolean;
    /** @internal Subscription used by the mint queue; returns an unsubscribe function. */
    onUpgrade(listener: () => void): () => void;
    /** @internal Marks the associated Turnstile attempt as settled, preserving its first reason. */
    markSettled(reason: TurnstileMintSettlementReason): void;
}

export function createTurnstileMintControl(initialPriority: TurnstileMintPriority): TurnstileMintControl {
    let priority = initialPriority;
    let settlementReason: TurnstileMintSettlementReason | undefined;
    let promotedBeforeSettlement = initialPriority === 'foreground';
    const listeners = new Set<() => void>();
    return {
        get priority() {
            return priority;
        },
        upgradeToForeground() {
            if (priority === 'foreground') return promotedBeforeSettlement;
            if (settlementReason !== undefined) return settlementReason === 'other';
            priority = 'foreground';
            promotedBeforeSettlement = true;
            for (const listener of [...listeners]) listener();
            return true;
        },
        onUpgrade(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        markSettled(reason) {
            if (settlementReason !== undefined) return;
            settlementReason = reason;
            listeners.clear();
        },
    };
}

// Tokens are minted one at a time — concurrent requests (e.g. parallel uploads) share one
// challenge surface and Turnstile widgets cannot safely overlap in it.
let queue: Promise<unknown> = Promise.resolve();

/** Handle over a mint whose widget is live, used by the queue's background-deadline logic. */
type ActiveMintControls = {
    /** Tear the widget down and resolve the mint null, freeing the queue slot immediately. */
    cancel: () => void;
};

function mintToken(
    siteKey: string,
    action: string,
    getPriority: () => TurnstileMintPriority,
    onActive: (controls: ActiveMintControls) => void,
    onEscalationDeclined: () => void,
): Promise<string | null> {
    const turnstile = window.turnstile;
    if (!turnstile) return Promise.resolve(null);

    const container = ensureChallengeSurface();

    return new Promise<string | null>((resolve) => {
        let widgetId: string | null = null;
        let settled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const finish = (token: string | null) => {
            if (settled) return;
            settled = true;
            if (timer !== undefined) {
                clearTimeout(timer);
            }
            if (widgetId !== null) {
                try {
                    turnstile.remove(widgetId);
                } catch {
                    // Widget already gone — nothing to clean up.
                }
            }
            removeChallengeSurface();
            resolve(token);
        };

        const armTimeout = (timeoutMs: number) => {
            if (timer !== undefined) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => finish(null), timeoutMs);
        };

        armTimeout(TOKEN_TIMEOUT_MS);
        onActive({
            cancel: () => finish(null),
        });

        try {
            widgetId = turnstile.render(container, {
                sitekey: siteKey,
                appearance: 'interaction-only',
                size: challengeWidgetSize(),
                action,
                // The mint owns retries: finish() removes the widget, so a Cloudflare retry would reset a dead widget.
                retry: 'never',
                callback: (token) => finish(token),
                'expired-callback': () => finish(null),
                'error-callback': (errorCode) => {
                    log.warn('Turnstile challenge failed', errorCode);
                    finish(null);
                    // Mark the error handled so Cloudflare schedules no retry or crash-retry timer.
                    return true;
                },
                'before-interactive-callback': () => {
                    if (getPriority() === 'background') {
                        // A speculative mint must neither pop a human challenge the
                        // claimant never asked for nor hold the serial queue for the
                        // interactive window. Fail it; the foreground action that follows
                        // mints again and owns the interactive experience.
                        onEscalationDeclined();
                        finish(null);
                        return;
                    }
                    // Escalation is the moment the corner anchor becomes a centred, dimmed
                    // overlay: a challenge nobody notices is a challenge nobody completes.
                    setChallengeInteractive(true);
                    // A claimant who has been escalated to an interactive challenge needs
                    // a fresh human-scale window rather than the remainder of the silent
                    // mint's short deadline.
                    armTimeout(INTERACTIVE_TOKEN_TIMEOUT_MS);
                },
                'after-interactive-callback': () => {
                    setChallengeInteractive(false);
                    armTimeout(TOKEN_TIMEOUT_MS);
                },
            });
        } catch (error) {
            log.warn('Turnstile render failed', error);
            finish(null);
        }
    });
}

export interface TurnstileMintOptions {
    /**
     * Priority handle for this mint. Omitted — or holding priority 'foreground' — keeps the
     * pre-existing behaviour: the mint waits its full queue turn and gets the standard
     * per-slot window. A control holding 'background' marks the mint speculative: the
     * caller's TOTAL wait (queue time included) is bounded by a short deadline, an entry
     * whose deadline fires is skipped or cancelled rather than left to occupy its slot, and
     * an interactive escalation fails the mint instead of surfacing a challenge. The
     * creator may later call `upgradeToForeground()` when a foreground actor joins the
     * request this mint serves.
     */
    mintControl?: TurnstileMintControl;
}

/**
 * Mint a single-use Cloudflare Turnstile token for a public-form request.
 *
 * Returns `null` when a token cannot be produced (site key missing, script blocked, challenge
 * failed or timed out) — callers send the request without a credential in that case and let
 * the server decide (per-org bypass and the operator kill-switch are honoured server-side).
 *
 * @param action - Turnstile action label shown in the Cloudflare analytics dashboard.
 *                 Must match `[a-zA-Z0-9_-]{0,32}`.
 */
export async function getTurnstileToken(action: string, options?: TurnstileMintOptions): Promise<string | null> {
    const mintControl = options?.mintControl;
    const siteKey = getConfig('TURNSTILE_SITE_KEY', import.meta.env.VITE_TURNSTILE_SITE_KEY);
    // A mint that ends without a token is reported once, with what ended it: the request that
    // follows goes out with no credential, and nothing else records why.
    const reportUnavailable = (reason: TurnstileUnavailableReason) =>
        reportTurnstileTokenUnavailable({ action, priority: mintControl?.priority ?? 'foreground', reason });

    if (!siteKey) {
        log.warn('Turnstile site key is not configured');
        mintControl?.markSettled('other');
        reportUnavailable('site-key-missing');
        return null;
    }

    let abandoned = false;
    let scriptReady = false;
    let scriptUnavailable = false;
    let runSettlementReason: TurnstileMintSettlementReason = 'other';
    const unavailableReason = (reason: TurnstileMintSettlementReason): TurnstileUnavailableReason => {
        if (reason !== 'other') return reason;
        return scriptUnavailable ? 'script-unavailable' : 'challenge-unresolved';
    };
    let activeMint: ActiveMintControls | null = null;
    let signalAbandonment: (() => void) | undefined;
    const abandonmentSignal = new Promise<void>((resolve) => {
        signalAbandonment = resolve;
    });

    const run = queue.then(async () => {
        // A background caller that already gave up must not spend a queue slot on a
        // script load and widget render whose token nobody will read.
        if (abandoned) return null;
        try {
            if (mintControl?.priority === 'background') {
                const scriptLoaded = await Promise.race([
                    loadTurnstileScript({ retryAfterFailure: true }).then(() => true),
                    abandonmentSignal.then(() => false),
                ]);
                if (!scriptLoaded) return null;
            } else {
                await loadTurnstileScript({ retryAfterFailure: true });
            }
            scriptReady = true;
            if (abandoned) return null;
            return await mintToken(
                siteKey,
                action.slice(0, 32),
                () => mintControl?.priority ?? 'foreground',
                (controls) => {
                    activeMint = controls;
                },
                () => {
                    runSettlementReason = 'escalation-declined';
                },
            );
        } catch (error) {
            log.warn('Turnstile token unavailable', error);
            scriptUnavailable = !scriptReady;
            return null;
        }
    });
    queue = run.catch(() => null);

    if (mintControl?.priority !== 'background') {
        return run
            .then((token) => {
                if (token === null) reportUnavailable(unavailableReason(runSettlementReason));
                return token;
            })
            .finally(() => mintControl?.markSettled('other'));
    }

    // Background (speculative) entry: bound the caller's total wait from enqueue, so a
    // hover-triggered mint can never hold foreground work — file-upload credentials, the
    // submit itself — behind a stalled challenge (CUR-901). On expiry the entry is
    // abandoned: still queued ⇒ skipped outright when its turn comes; mid-mint ⇒ cancelled
    // so the next entry proceeds immediately.
    return new Promise<string | null>((resolve) => {
        let deadline: ReturnType<typeof setTimeout> | undefined;
        let detachUpgrade: (() => void) | undefined;
        let settled = false;

        const settle = (token: string | null, reason: TurnstileMintSettlementReason) => {
            if (settled) return;
            settled = true;
            if (deadline !== undefined) {
                clearTimeout(deadline);
                deadline = undefined;
            }
            detachUpgrade?.();
            mintControl.markSettled(reason);
            if (token === null) reportUnavailable(unavailableReason(reason));
            resolve(token);
        };

        deadline = setTimeout(() => {
            abandoned = true;
            signalAbandonment?.();
            activeMint?.cancel();
            settle(null, 'deadline');
        }, BACKGROUND_TOKEN_DEADLINE_MS);

        // A foreground caller has joined the request this mint serves (e.g. a tap joining
        // a hover-initiated checkpoint save): drop the speculative deadline. An in-flight
        // widget keeps the standard timeout measured from render, while a still-queued
        // entry starts a full standard window when its turn comes.
        detachUpgrade = mintControl.onUpgrade(() => {
            if (deadline !== undefined) {
                clearTimeout(deadline);
                deadline = undefined;
            }
        });

        void run.then((token) => settle(token, runSettlementReason));
    });
}
